import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeft,
  Edit,
  Calendar,
  User,
  Eye,
  ThumbsUp,
  ThumbsDown,
  Tag,
  Folder,
  Clock,
  BookOpen
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn, formatDateTime } from '@/lib/utils';
import api from '@/services/api';
import { useToast } from '@/hooks/useToast';

interface KBArticle {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt?: string | null;
  category?: {
    id: string;
    name: string;
    slug: string;
  };
  tags: string[] | null;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  isFeatured: boolean;
  viewCount: number;
  helpfulCount: number;
  notHelpfulCount: number;
  publishedAt: string | null;
  author?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  relatedArticles?: KBArticle[];
  createdAt: string;
  updatedAt: string;
}

export default function KBArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  const [feedbackGiven, setFeedbackGiven] = useState<'yes' | 'no' | null>(null);

  // Fetch article
  const { data: article, isLoading, error } = useQuery({
    queryKey: ['kbArticle', id],
    queryFn: async () => {
      const response = await api.get(`/knowledge-base/articles/${id}`);
      return response.data.data as KBArticle;
    },
    enabled: !!id,
  });

  // Fetch related articles
  const { data: relatedArticles } = useQuery({
    queryKey: ['kbRelatedArticles', id],
    queryFn: async () => {
      const response = await api.get(`/knowledge-base/articles/${id}/related`);
      return response.data.data as KBArticle[];
    },
    enabled: !!id,
  });

  // Helpful feedback mutation
  const feedbackMutation = useMutation({
    mutationFn: async (helpful: boolean) => {
      return api.post(`/knowledge-base/articles/${id}/feedback`, { helpful });
    },
    onSuccess: (_, helpful) => {
      setFeedbackGiven(helpful ? 'yes' : 'no');
      queryClient.invalidateQueries({ queryKey: ['kbArticle', id] });
      toast({ 
        title: 'Thank you!', 
        description: 'Your feedback has been recorded.' 
      });
    },
    onError: () => {
      toast({ 
        title: 'Error', 
        description: 'Failed to submit feedback', 
        variant: 'destructive' 
      });
    },
  });

  const handleFeedback = (helpful: boolean) => {
    if (feedbackGiven) return;
    feedbackMutation.mutate(helpful);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PUBLISHED':
        return 'bg-green-100 text-green-800';
      case 'DRAFT':
        return 'bg-yellow-100 text-yellow-800';
      case 'ARCHIVED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="text-center py-12">
        <BookOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-lg font-medium">Article not found</p>
        <p className="text-muted-foreground mb-4">
          The article you're looking for doesn't exist or has been removed.
        </p>
        <Button asChild>
          <Link to="/knowledge-base">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Knowledge Base
          </Link>
        </Button>
      </div>
    );
  }

  const helpfulTotal = article.helpfulCount + article.notHelpfulCount;
  const helpfulPercentage = helpfulTotal > 0 
    ? Math.round((article.helpfulCount / helpfulTotal) * 100) 
    : null;

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/knowledge-base')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Knowledge Base
        </Button>
        <Button asChild>
          <Link to={`/knowledge-base/${id}/edit`}>
            <Edit className="w-4 h-4 mr-2" />
            Edit Article
          </Link>
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-2">
                  <CardTitle className="text-2xl">{article.title}</CardTitle>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {article.author 
                        ? `${article.author.firstName} ${article.author.lastName}` 
                        : 'Unknown'}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDateTime(article.createdAt)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      Updated {formatDateTime(article.updatedAt)}
                    </span>
                  </div>
                </div>
                <span className={cn(
                  "px-2 py-1 text-xs font-medium rounded-full shrink-0",
                  getStatusBadge(article.status)
                )}>
                  {article.status}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              {/* Article Content - Render as HTML/Markdown */}
              <div 
                className="prose prose-sm max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </CardContent>
          </Card>

          {/* Feedback Section */}
          <Card>
            <CardContent className="p-6">
              <div className="text-center space-y-4">
                <p className="font-medium">Was this article helpful?</p>
                <div className="flex items-center justify-center gap-4">
                  <Button
                    variant={feedbackGiven === 'yes' ? 'default' : 'outline'}
                    onClick={() => handleFeedback(true)}
                    disabled={feedbackGiven !== null || feedbackMutation.isPending}
                    className={cn(
                      feedbackGiven === 'yes' && "bg-green-600 hover:bg-green-700"
                    )}
                  >
                    <ThumbsUp className="w-4 h-4 mr-2" />
                    Yes
                  </Button>
                  <Button
                    variant={feedbackGiven === 'no' ? 'default' : 'outline'}
                    onClick={() => handleFeedback(false)}
                    disabled={feedbackGiven !== null || feedbackMutation.isPending}
                    className={cn(
                      feedbackGiven === 'no' && "bg-red-600 hover:bg-red-700"
                    )}
                  >
                    <ThumbsDown className="w-4 h-4 mr-2" />
                    No
                  </Button>
                </div>
                {feedbackGiven && (
                  <p className="text-sm text-muted-foreground">
                    Thank you for your feedback!
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Article Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Article Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Views
                </span>
                <span className="font-medium">{article.viewCount.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground flex items-center gap-2">
                  <ThumbsUp className="w-4 h-4" />
                  Helpful
                </span>
                <span className="font-medium">
                  {helpfulPercentage !== null ? (
                    <span className={cn(
                      helpfulPercentage >= 70 ? "text-green-600" : 
                      helpfulPercentage >= 50 ? "text-yellow-600" : "text-red-600"
                    )}>
                      {helpfulPercentage}% ({helpfulTotal} votes)
                    </span>
                  ) : (
                    <span className="text-muted-foreground">No votes yet</span>
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground flex items-center gap-2">
                  <Folder className="w-4 h-4" />
                  Category
                </span>
                <span className="font-medium">{article.category?.name || '-'}</span>
              </div>
            </CardContent>
          </Card>

          {/* Tags */}
          {article.tags && article.tags.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Tags
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag, idx) => (
                    <span 
                      key={idx} 
                      className="px-3 py-1 text-sm bg-muted rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Related Articles */}
          {relatedArticles && relatedArticles.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  Related Articles
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {relatedArticles.map((related) => (
                    <li key={related.id}>
                      <Link 
                        to={`/knowledge-base/${related.id}`}
                        className="text-sm hover:text-primary transition-colors line-clamp-2"
                      >
                        {related.title}
                      </Link>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        <Eye className="w-3 h-3" />
                        {related.viewCount} views
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
